import { entryToIndication, IndicationItem } from 'features/indicationTriage'
import { useRolodexEntries } from 'features/rolodex'
import _ from 'lodash'
import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react'
import {
  GeckoAggregation,
  GeckoAlgorithm,
  GeckoAnglePlotSetting,
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoInspection,
  GeckoOrganization,
  GeckoSite,
  GeckoSource,
  GeckoUnit,
  GeckoView,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

export interface IndicationContextType {
  site: GeckoSite | undefined
  organization: GeckoOrganization | undefined
  unit: GeckoUnit | undefined
  indication: IndicationItem | undefined
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  relevantThicknessCDF: GeckoComponentDataFrame | undefined
  assetModel: GeckoAssetModel | undefined
  component: GeckoComponent | undefined
  colorScaleThreshold: GeckoColorScaleThreshold | undefined
  isLoading: boolean
  forceRefresh: () => Promise<void>
  inspection: GeckoInspection | undefined
}

export const IndicationContext = createContext<
  IndicationContextType | undefined
>(undefined)

export interface IndicationContextProviderProps {
  indicationId: string
  children: ReactNode
}

function extractFirstIndication(entries: ReadonlyArray<GeckoEntry>): {
  indication: IndicationItem | null
  tags: RolodexEntryTagsType | null
} {
  const entry = _.find(entries, (e) => e.type === 'indication')
  return {
    indication: entry ? entryToIndication(entry) : null,
    tags: entry?.tags ?? null,
  }
}

/**
 * Provides an IndicationContext direct from Rolodex, for use when not in the
 * details page, for example Drilldown.
 */
export const RolodexIndicationContextProvider: FC<{
  indicationId: string
  children: ReactNode
}> = ({ indicationId, children }) => {
  const {
    entries: rawEntriesForIndications,
    isLoading,
    components,
    units,
    inspections,
    sites,
    organizations,
    forceRefresh: indicationRefresh,
  } = useRolodexEntries(
    useMemo(
      () => ({
        ids: [indicationId],
        links: [
          RolodexEntryType.COMPONENT,
          RolodexEntryType.UNIT,
          RolodexEntryType.INSPECTION,
          RolodexEntryType.SITE,
          RolodexEntryType.ORGANIZATION,
        ],
      }),
      [indicationId]
    ),
    { allowCache: false }
  )

  const { indication, tags: indicationTags } = useMemo(
    () => extractFirstIndication(rawEntriesForIndications),
    [rawEntriesForIndications]
  )

  // We try to pick all these based on indpsection,
  // as it's the closest relation to the indication,
  // but we need to handle legacy indications which
  // were tagged to just the component, or in some
  // cases the unit.
  const {
    componentDataFrames,
    componentDefaults,
    assetModels,
    forceRefresh: derivativeRefresh,
  } = useRolodexEntries(
    useMemo(
      () =>
        indicationTags
          ? [
              {
                tags: _.pick(
                  indicationTags,
                  indicationTags.inspection
                    ? 'inspection'
                    : indicationTags.component
                      ? 'component'
                      : 'unit'
                ),
                type: [
                  RolodexEntryType.COMPONENT_DATA_FRAME,
                  RolodexEntryType.COMPONENT_DEFAULTS,
                ],
              },
              {
                type: RolodexEntryType.ASSET_MODEL,
                tags: {
                  unit: indicationTags.unit,
                },
              },
            ]
          : [],
      [indicationTags]
    )
  )

  const { relevantThicknessCDF } = useRelevantCdfs(
    _.find(
      componentDataFrames,
      (cdf) => cdf.data.params.view.id === 'thickness'
    ),
    componentDataFrames
  )

  const contextValue = useMemo<IndicationContextType | undefined>(
    () => ({
      site: _.first(sites),
      organization: _.first(organizations),
      indication: indication ?? undefined,
      component: _.first(components),
      unit: _.first(units),
      componentDataFrames,
      assetModel: _.first(assetModels),
      inspection: _.first(inspections),
      colorScaleThreshold:
        _.first(componentDefaults)?.data.color_scale_thresholds.get(
          'thickness'
        ),
      relevantThicknessCDF,
      isLoading,
      forceRefresh: async () => {
        await indicationRefresh()
        await derivativeRefresh()
      },
    }),
    [
      assetModels,
      componentDataFrames,
      componentDefaults,
      components,
      derivativeRefresh,
      indication,
      indicationRefresh,
      inspections,
      isLoading,
      organizations,
      relevantThicknessCDF,
      sites,
      units,
    ]
  )
  return (
    <IndicationContext.Provider value={contextValue}>
      {children}
    </IndicationContext.Provider>
  )
}

export function useIndicationData(): IndicationContextType {
  const indicationContext = useContext(IndicationContext)
  if (!indicationContext) {
    throw new Error(
      'useIndicationData() must be used within an indication context'
    )
  }
  return indicationContext
}

function isInspectionThicknessCDF(cdf: GeckoComponentDataFrame) {
  return (
    cdf.data.params.view === GeckoView.Thickness &&
    cdf.data.params.source !== GeckoSource.Projection &&
    cdf.data.params.source !== GeckoSource.TMin &&
    cdf.data.params.source !== GeckoSource.Nominal
  )
}

const useRelevantCdfs = (
  underlyingCDF: GeckoComponentDataFrame | undefined,
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
) =>
  React.useMemo(() => {
    const cdfsWithSameCDS = !underlyingCDF
      ? []
      : componentDataFrames.filter(
          (cdf) =>
            cdf.tags['component-dataset'] ===
            underlyingCDF.tags['component-dataset']
        )
    const allRelevantThicknessCDFs = !underlyingCDF
      ? []
      : cdfsWithSameCDS.filter((cdf) => {
          const { bin_size } = cdf.data.binning
          const { component, inspection } = cdf.tags

          return (
            component === underlyingCDF.tags.component &&
            inspection === underlyingCDF.tags.inspection &&
            bin_size === underlyingCDF.data.binning.bin_size &&
            isInspectionThicknessCDF(cdf)
          )
        })
    return {
      relevantThicknessCDF: allRelevantThicknessCDFs.find(
        (cdf) =>
          // Check GeckoAggregation
          (!underlyingCDF ||
          underlyingCDF.data.params.aggregation === GeckoAggregation.None
            ? cdf.data.params.aggregation === GeckoAggregation.Minimum
            : cdf.data.params.aggregation ===
              underlyingCDF.data.params.aggregation) &&
          // Check GeckoAnglePlotSetting
          (!underlyingCDF ||
          underlyingCDF.data.params.angle_plot_setting ===
            GeckoAnglePlotSetting.None
            ? true
            : cdf.data.params.angle_plot_setting ===
              underlyingCDF.data.params.angle_plot_setting) &&
          // Check GeckoAlgorithm
          (!underlyingCDF ||
            underlyingCDF.data.params.algorithm === GeckoAlgorithm.None ||
            underlyingCDF.data.params.algorithm ===
              cdf.data.params.algorithm) &&
          // Check GeckoBinSize
          (!underlyingCDF ||
            underlyingCDF.data.binning.bin_size === cdf.data.binning.bin_size)
      ),
    }
  }, [componentDataFrames, underlyingCDF])
